import React, { useState, useEffect } from 'react';
import { useNavigate, useSearchParams } from 'react-router-dom';
import {
  Box, Card, CardContent, Grid, Typography, Button, Chip, TextField, 
  FormControl, InputLabel, Select, MenuItem, Dialog, DialogTitle, 
  DialogContent, DialogActions, IconButton, CircularProgress, Avatar,
  Tabs, Tab, Table, TableBody, TableCell, TableContainer, TableHead, 
  TableRow, Paper, InputAdornment, Tooltip,   FormHelperText,
  Alert, FormControlLabel, Checkbox, Stack
} from '@mui/material';
import {
  Add as AddIcon,
  ArrowBack as BackIcon,
  Edit as EditIcon,
  Delete as DeleteIcon,
  YouTube as YouTubeIcon,
  Article as ArticleIcon,
  MenuBook as WikiIcon,
  Headphones as AudioIcon,
  Podcasts as PodcastIcon,
  Link as LinkIcon,
  PictureAsPdf as PdfIcon,
  PlayCircle as VideoIcon,
  GitHub as GithubIcon,
  Collections as SlidesIcon,
  Quiz as FlashcardIcon,
  Category as CategoryIcon,
  School as TrainingIcon,
  Search as SearchIcon,
  People as UsersIcon,
  Place as LearningSpotTabIcon,
  Quiz as QuizIcon,
  ArrowForward as ArrowForwardIcon,
  Topic as TopicIcon,
} from '@mui/icons-material';
import { toast } from 'react-toastify';
import api from '../../utils/api';
import { useAuth } from '../../context/AuthContext';
import { RequiredLabel, getFieldErrorSx, getSelectErrorSx } from '../../components/common/FormHelpers';
import LearningSpotsCourseAdmin from './LearningSpotsCourseAdmin';
import TrainingExamAdmin from './TrainingExamAdmin';
import TrainingYoutubeLibrary from './TrainingYoutubeLibrary';

// Content type configuration
const contentTypes = [
  { value: 'youtube', label: 'YouTube', icon: YouTubeIcon, color: '#FF0000' },
  { value: 'udemy', label: 'Udemy', icon: VideoIcon, color: '#A435F0' },
  { value: 'coursera', label: 'Coursera', icon: VideoIcon, color: '#0056D2' },
  { value: 'wiki', label: 'Wiki', icon: WikiIcon, color: '#333333' },
  { value: 'medium', label: 'Medium', icon: ArticleIcon, color: '#00AB6C' },
  { value: 'article', label: 'Article', icon: ArticleIcon, color: '#1a73e8' },
  { value: 'document', label: 'Document', icon: ArticleIcon, color: '#4285F4' },
  { value: 'pdf', label: 'PDF', icon: PdfIcon, color: '#DB4437' },
  { value: 'video', label: 'Video', icon: VideoIcon, color: '#FF6B6B' },
  { value: 'audio', label: 'Audio', icon: AudioIcon, color: '#1DB954' },
  { value: 'podcast', label: 'Podcast', icon: PodcastIcon, color: '#8940FA' },
  { value: 'flashcard', label: 'Flashcards', icon: FlashcardIcon, color: '#FF9800' },
  { value: 'ebook', label: 'E-Book', icon: WikiIcon, color: '#6D4C41' },
  { value: 'slides', label: 'Slides', icon: SlidesIcon, color: '#FBBC04' },
  { value: 'github', label: 'GitHub', icon: GithubIcon, color: '#333' },
  { value: 'reference', label: 'Reference', icon: LinkIcon, color: '#607D8B' },
  { value: 'url', label: 'URL/Other', icon: LinkIcon, color: '#9E9E9E' }
];

const difficultyColors = { 
  beginner: 'success', 
  intermediate: 'warning', 
  advanced: 'error' 
};

/** Sync with ?tab= for sidebar deep links (e.g. /training/admin?tab=exams) */
const ADMIN_TAB_KEYS = ['categories', 'courses', 'materials', 'youtube-library', 'exams', 'assignments'];

const CATEGORY_BOX_PALETTE = [
  { bg: '#E8F4FC', border: '#4A90C8' },
  { bg: '#EEF8F1', border: '#3D9B6A' },
  { bg: '#FFF4E5', border: '#E6A023' },
  { bg: '#F3E8FF', border: '#9B6BC7' },
  { bg: '#FFE8EE', border: '#D45B7A' },
  { bg: '#E6F7F5', border: '#2A9B91' },
  { bg: '#FEF3C7', border: '#D97706' },
  { bg: '#E0E7FF', border: '#6366F1' }
];

const TrainingMaterials = () => {
  const navigate = useNavigate();
  const [searchParams, setSearchParams] = useSearchParams();
  const { hasPermission } = useAuth();
  const [activeTab, setActiveTab] = useState(() => {
    if (typeof window === 'undefined') return 0;
    const t = new URLSearchParams(window.location.search).get('tab');
    const idx = t ? ADMIN_TAB_KEYS.indexOf(t) : -1;
    return idx >= 0 ? idx : 0;
  });
  const [loading, setLoading] = useState(true);
  
  // Categories
  const [categories, setCategories] = useState([]);
  const [categoryDialog, setCategoryDialog] = useState(false);
  const [categoryForm, setCategoryForm] = useState({
    name: '',
    description: '',
    color: '#6366f1',
    icon: 'school'
  });
  const [editingCategoryId, setEditingCategoryId] = useState(null);

  // Materials
  const [materials, setMaterials] = useState([]);
  const [materialDialog, setMaterialDialog] = useState(false);
  const [materialForm, setMaterialForm] = useState({
    title: '', description: '', category_id: '', content_type: 'youtube',
    content_url: '', duration_minutes: '', difficulty_level: 'beginner',
    is_mandatory: false, author: '', tags: '', thumbnail_url: ''
  });
  const [editingMaterialId, setEditingMaterialId] = useState(null);
  const [search, setSearch] = useState('');
  const [filterCategory, setFilterCategory] = useState('');

  // Assignments
  const [enrollments, setEnrollments] = useState([]);
  const [users, setUsers] = useState([]);
  const [assignDialog, setAssignDialog] = useState(false);
  const [assignForm, setAssignForm] = useState({ user_id: '', category_id: '', due_date: '' });
  
  // Validation errors
  const [materialErrors, setMaterialErrors] = useState({});
  const [categoryErrors, setCategoryErrors] = useState({});
  const [spotsByCategory, setSpotsByCategory] = useState({});
  const [loadingSpotsByCategory, setLoadingSpotsByCategory] = useState(false);

  const canAdmin = hasPermission('training_admin') || hasPermission('training_add');

  useEffect(() => { fetchData(); }, []);

  useEffect(() => {
    const t = searchParams.get('tab');
    const idx = t ? ADMIN_TAB_KEYS.indexOf(t) : -1;
    if (idx >= 0) setActiveTab(idx);
    const cid = searchParams.get('categoryId');
    if (cid) setFilterCategory(cid);
  }, [searchParams]);

  // Prefetch sub-categories for Categories → Design courses navigation
  useEffect(() => {
    const designCats = categories.filter((c) => !c.is_system_category);
    if (!designCats.length) {
      setSpotsByCategory({});
      return;
    }
    let cancelled = false;
    (async () => {
      setLoadingSpotsByCategory(true);
      const map = {};
      await Promise.all(
        designCats.map(async (c) => {
          try {
            const r = await api.get('/training/learning-spots', { params: { category_id: c.id } });
            map[c.id] = r.data.data || [];
          } catch {
            map[c.id] = [];
          }
        })
      );
      if (!cancelled) {
        setSpotsByCategory(map);
        setLoadingSpotsByCategory(false);
      }
    })();
    return () => {
      cancelled = true;
    };
  }, [categories]);

  const goToTab = (index, { categoryId, spotId } = {}) => {
    setActiveTab(index);
    const p = new URLSearchParams();
    p.set('tab', ADMIN_TAB_KEYS[index] || 'categories');
    if (index === 1 && categoryId) {
      p.set('categoryId', categoryId);
      if (spotId) p.set('spotId', spotId);
    }
    setSearchParams(p, { replace: true });
  };

  const goDesignCurriculum = (categoryId, spotId = null) => {
    goToTab(1, { categoryId, spotId: spotId || undefined });
  };

  const fetchData = async () => {
    try {
      const [catRes, matRes, enrollRes, usersRes] = await Promise.all([
        api.get('/training/categories', { params: { include_system_categories: 'true' } }),
        api.get('/training/materials?limit=200'),
        canAdmin ? api.get('/training/admin/enrollments?limit=100') : Promise.resolve({ data: { data: [] } }),
        canAdmin ? api.get('/training/admin/users') : Promise.resolve({ data: { data: [] } })
      ]);
      setCategories(catRes.data.data || []);
      setMaterials(matRes.data.data || []);
      setEnrollments(enrollRes.data.data || []);
      setUsers(usersRes.data.data || []);
    } catch (e) { console.error(e); }
    finally { setLoading(false); }
  };

  // Category handlers
  const handleSaveCategory = async () => {
    // Validate required fields
    if (!categoryForm.name?.trim()) {
      setCategoryErrors({ name: true });
      toast.error('Please fill in all required fields marked with *');
      return;
    }
    
    try {
      if (editingCategoryId) {
        await api.put(`/training/categories/${editingCategoryId}`, categoryForm);
      } else {
        await api.post('/training/categories', categoryForm);
      }
      toast.success(`Category ${editingCategoryId ? 'updated' : 'created'}`);
      setCategoryDialog(false);
      setCategoryForm({ name: '', description: '', color: '#6366f1', icon: 'school' });
      setCategoryErrors({});
      setEditingCategoryId(null);
      fetchData();
    } catch (e) { toast.error(e.response?.data?.message || 'Failed to save'); }
  };

  const handleEditCategory = (cat) => {
    setCategoryForm({
      name: cat.name,
      description: cat.description || '',
      color: cat.color || '#6366f1',
      icon: cat.icon || 'school'
    });
    setEditingCategoryId(cat.id);
    setCategoryDialog(true);
  };

  const handleDeleteCategory = async (id) => {
    const cat = categories.find((c) => c.id === id);
    if (cat?.is_system_category) {
      toast.error('The Standalone exam catalog category is managed by the system and cannot be deleted.');
      return;
    }
    if (!window.confirm('Delete this category? Materials will be preserved.')) return;
    try {
      await api.delete(`/training/categories/${id}`);
      toast.success('Category deleted');
      fetchData();
    } catch (e) { toast.error('Failed to delete'); }
  };

  // Material handlers
  const handleSaveMaterial = async () => {
    // Frontend validation
    const errors = {};
    if (!materialForm.title?.trim()) {
      errors.title = true;
    }
    if (!materialForm.category_id) {
      errors.category_id = true;
    }
    if (!materialForm.content_type) {
      errors.content_type = true;
    }
    
    if (Object.keys(errors).length > 0) {
      setMaterialErrors(errors);
      toast.error('Please fill in all required fields marked with *');
      return;
    }
    
    try {
      const data = { ...materialForm };
      if (data.duration_minutes) data.duration_minutes = parseInt(data.duration_minutes);
      
      if (editingMaterialId) {
        await api.put(`/training/materials/${editingMaterialId}`, data);
      } else {
        await api.post('/training/materials', data);
      }
      toast.success(`Material ${editingMaterialId ? 'updated' : 'created'}`);
      setMaterialDialog(false);
      setMaterialForm({
        title: '', description: '', category_id: '', content_type: 'youtube',
        content_url: '', duration_minutes: '', difficulty_level: 'beginner',
        is_mandatory: false, author: '', tags: '', thumbnail_url: ''
      });
      setMaterialErrors({});
      setEditingMaterialId(null);
      fetchData();
    } catch (e) { 
      const errorMsg = e.response?.data?.errors?.[0]?.msg || e.response?.data?.message || 'Failed to save';
      toast.error(errorMsg); 
    }
  };

  // Clear material error on change
  const handleMaterialFormChange = (field, value) => {
    setMaterialForm({ ...materialForm, [field]: value });
    if (materialErrors[field]) {
      setMaterialErrors(prev => {
        const newErrors = { ...prev };
        delete newErrors[field];
        return newErrors;
      });
    }
  };

  const handleEditMaterial = (mat) => {
    setMaterialForm({
      title: mat.title,
      description: mat.description || '',
      category_id: mat.category_id,
      content_type: mat.content_type,
      content_url: mat.content_url || '',
      duration_minutes: mat.duration_minutes || '',
      difficulty_level: mat.difficulty_level,
      is_mandatory: mat.is_mandatory,
      author: mat.author || '',
      tags: mat.tags || '',
      thumbnail_url: mat.thumbnail_url || ''
    });
    setEditingMaterialId(mat.id);
    setMaterialDialog(true);
  };

  const handleDeleteMaterial = async (id) => {
    if (!window.confirm('Delete this material?')) return;
    try {
      await api.delete(`/training/materials/${id}`);
      toast.success('Material deleted');
      fetchData();
    } catch (e) { toast.error('Failed to delete'); }
  };

  // Assignment handlers
  const handleAssign = async () => {
    try {
      await api.post('/training/admin/assign', assignForm);
      toast.success('Training assigned successfully');
      setAssignDialog(false);
      setAssignForm({ user_id: '', category_id: '', due_date: '' });
      fetchData();
    } catch (e) { toast.error(e.response?.data?.message || 'Failed to assign'); }
  };

  // Filter materials
  const filteredMaterials = materials.filter(mat => {
    const matchesSearch = !search || 
      mat.title.toLowerCase().includes(search.toLowerCase()) ||
      mat.description?.toLowerCase().includes(search.toLowerCase());
    const matchesCategory = !filterCategory || mat.category_id === filterCategory;
    return matchesSearch && matchesCategory;
  });

  if (loading) {
    return <Box sx={{ display: 'flex', justifyContent: 'center', py: 8 }}><CircularProgress /></Box>;
  }

  return (
    <Box>
      {/* Header */}
      <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', mb: 3 }}>
        <Box sx={{ display: 'flex', alignItems: 'center', gap: 2 }}>
          <IconButton onClick={() => navigate('/training/catalog')}>
            <BackIcon />
          </IconButton>
          <Box>
            <Typography variant="h4" sx={{ fontWeight: 700 }}>Training Administration</Typography>
            <Typography variant="body2" color="text.secondary">
              Categories, sub-categories, courses, materials, exams, and assignments
            </Typography>
          </Box>
        </Box>
      </Box>

      {/* Tabs */}
      <Box sx={{ borderBottom: 1, borderColor: 'divider', mb: 2 }}>
        <Tabs value={activeTab} onChange={(e, v) => goToTab(v)}>
          <Tab icon={<CategoryIcon />} label="Categories" iconPosition="start" />
          <Tab icon={<LearningSpotTabIcon />} label="Sub-categories & courses" iconPosition="start" />
          <Tab icon={<TrainingIcon />} label="Materials" iconPosition="start" />
          <Tab icon={<YouTubeIcon />} label="YouTube Library" iconPosition="start" />
          <Tab icon={<QuizIcon />} label="Exams" iconPosition="start" />
          <Tab icon={<UsersIcon />} label="Assignments" iconPosition="start" />
        </Tabs>
      </Box>

      <Alert severity="info" sx={{ mb: 3, borderRadius: 2 }}>
        {activeTab === 0 && (
          <Typography variant="body2">
            <strong>Categories</strong> are top-level programs (e.g. Adobe Training). Click a category or a sub-category to open{' '}
            <strong>Sub-categories &amp; courses</strong> and design outlines.
          </Typography>
        )}
        {activeTab === 1 && (
          <Typography variant="body2">
            <strong>Sub-categories</strong> organize tracks (e.g. Experience Platform). <strong>Courses</strong> are individual offerings (ILT or self-paced).{' '}
            <strong>Published</strong> courses show in Browse Catalog. Build the day-by-day outline with <strong>Course outline</strong>, then link items from the Materials library.
          </Typography>
        )}
        {activeTab === 2 && (
          <Typography variant="body2">
            <strong>Materials</strong> are the reusable library: upload or link videos, PDFs, URLs, etc., each tagged to a category. They do not appear in the catalog by themselves — you attach them inside a{' '}
            <strong>course outline</strong> (Sub-categories tab) using <strong>Link material</strong>, or swap content into a placeholder row.
          </Typography>
        )}
        {activeTab === 3 && (
          <Typography variant="body2">
            <strong>YouTube Library</strong> registers public channels and pulls video metadata only (no file download).
            Videos start private; publish them for members. Group into course names and sync public items into Materials.
          </Typography>
        )}
        {activeTab === 4 && (
          <Typography variant="body2">
            <strong>Exams</strong> can attach to courses or be listed in the built-in <strong>Standalone exam catalog</strong> category: use the exam dialog checkbox and publish. That category is created by the system and cannot be deleted. Questions use visual editors; JSON is stored for grading.
          </Typography>
        )}
        {activeTab === 5 && (
          <Typography variant="body2">
            <strong>Assignments</strong> enroll users on legacy category-based training. Catalog enrollment uses Browse Catalog; assignments here are for admins distributing material lists.
          </Typography>
        )}
      </Alert>

      {/* Categories Tab */}
      {activeTab === 0 && (
        <Box>
          <Box sx={{ display: 'flex', justifyContent: 'flex-end', mb: 2 }}>
            <Button 
              variant="contained" 
              startIcon={<AddIcon />} 
              onClick={() => {
                setEditingCategoryId(null);
                const nextPalette =
                  CATEGORY_BOX_PALETTE[
                    categories.filter((c) => !c.is_system_category).length % CATEGORY_BOX_PALETTE.length
                  ];
                setCategoryForm({
                  name: '',
                  description: '',
                  color: nextPalette.border,
                  icon: 'school'
                });
                setCategoryDialog(true);
              }}
              sx={{ background: 'linear-gradient(135deg, #6366f1 0%, #8b5cf6 100%)' }}
            >
              Add Category
            </Button>
          </Box>
          
          <Grid container spacing={3}>
            {categories.filter((c) => !c.is_system_category).map((cat, idx) => {
              const palette = CATEGORY_BOX_PALETTE[idx % CATEGORY_BOX_PALETTE.length];
              return (
              <Grid item xs={12} sm={6} md={4} key={cat.id}>
                <Card
                  sx={{
                    height: '100%',
                    bgcolor: palette.bg,
                    border: '2px solid',
                    borderColor: palette.border,
                    borderRadius: 2,
                    boxShadow: '0 2px 10px rgba(15, 40, 60, 0.08)'
                  }}
                >
                  <CardContent>
                    <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'flex-start', mb: 2 }}>
                      <Avatar
                        sx={{ bgcolor: cat.color || '#6366f1', width: 48, height: 48, cursor: 'pointer' }}
                        onClick={() => goDesignCurriculum(cat.id)}
                        title="Design courses in this category"
                      >
                        <TrainingIcon />
                      </Avatar>
                      <Box>
                        <IconButton size="small" onClick={() => handleEditCategory(cat)}>
                          <EditIcon fontSize="small" />
                        </IconButton>
                        <IconButton size="small" color="error" onClick={() => handleDeleteCategory(cat.id)}>
                          <DeleteIcon fontSize="small" />
                        </IconButton>
                      </Box>
                    </Box>
                    <Typography
                      variant="h6"
                      sx={{ fontWeight: 600, cursor: 'pointer', '&:hover': { color: 'primary.main' } }}
                      onClick={() => goDesignCurriculum(cat.id)}
                    >
                      {cat.short_id ? `[${cat.short_id}] ` : ''}{cat.name}
                    </Typography>
                    <Typography variant="body2" color="text.secondary" sx={{ mb: 1.5, minHeight: 40 }}>
                      {cat.description || 'No description'}
                    </Typography>
                    <Stack direction="row" spacing={1} flexWrap="wrap" useFlexGap sx={{ mb: 1.5 }}>
                      <Chip
                        label={`${materials.filter(m => m.category_id === cat.id).length} materials`}
                        size="small"
                        variant="outlined"
                      />
                      <Chip
                        label={`${(spotsByCategory[cat.id] || []).length} sub-categories`}
                        size="small"
                        variant="outlined"
                        color="primary"
                      />
                    </Stack>
                    <Typography variant="caption" color="text.secondary" display="block" sx={{ mb: 0.75 }}>
                      Sub-categories — click to design courses
                    </Typography>
                    {loadingSpotsByCategory && !spotsByCategory[cat.id] ? (
                      <CircularProgress size={18} />
                    ) : (spotsByCategory[cat.id] || []).length === 0 ? (
                      <Typography variant="caption" color="text.secondary">
                        No sub-categories yet — open Design courses to add one.
                      </Typography>
                    ) : (
                      <Stack direction="row" flexWrap="wrap" gap={0.75} sx={{ mb: 1.5 }}>
                        {(spotsByCategory[cat.id] || []).map((spot) => (
                          <Chip
                            key={spot.id}
                            size="small"
                            icon={<TopicIcon />}
                            label={spot.name}
                            onClick={() => goDesignCurriculum(cat.id, spot.id)}
                            clickable
                            color="primary"
                            variant="outlined"
                          />
                        ))}
                      </Stack>
                    )}
                    <Button
                      size="small"
                      variant="contained"
                      endIcon={<ArrowForwardIcon />}
                      onClick={() => goDesignCurriculum(cat.id)}
                      fullWidth
                    >
                      Design courses
                    </Button>
                  </CardContent>
                </Card>
              </Grid>
              );
            })}
          </Grid>
          <Alert severity="info" sx={{ mt: 2 }}>
            The <strong>Standalone exam catalog</strong> category is created automatically for exam-only offerings. It is hidden from this list and managed under the <strong>Exams</strong> tab when you select it in the category filter.
          </Alert>
        </Box>
      )}

      {/* Learning spots & structured titles */}
      {activeTab === 1 && (
        <LearningSpotsCourseAdmin
          categories={categories.filter((c) => !c.is_system_category)}
          materials={materials}
          users={users}
          onOpenCategoriesTab={() => goToTab(0)}
          onOpenMaterialsTab={() => goToTab(2)}
          onMaterialsRefresh={fetchData}
          focusCategoryId={searchParams.get('categoryId') || ''}
          focusSpotId={searchParams.get('spotId') || ''}
        />
      )}

      {/* Materials Tab */}
      {activeTab === 2 && (
        <Box>
          <Box sx={{ display: 'flex', gap: 2, mb: 3 }}>
            <TextField
              placeholder="Search materials..."
              value={search}
              onChange={(e) => setSearch(e.target.value)}
              InputProps={{
                startAdornment: <InputAdornment position="start"><SearchIcon /></InputAdornment>
              }}
              sx={{ flex: 1 }}
            />
            <FormControl sx={{ minWidth: 200 }}>
              <InputLabel>Category</InputLabel>
              <Select
                value={filterCategory}
                label="Category"
                onChange={(e) => setFilterCategory(e.target.value)}
              >
                <MenuItem value="">All Categories</MenuItem>
                {categories.filter((c) => !c.is_system_category).map(c => <MenuItem key={c.id} value={c.id}>{c.name}</MenuItem>)}
              </Select>
            </FormControl>
            <Button 
              variant="contained" 
              startIcon={<AddIcon />} 
              onClick={() => setMaterialDialog(true)}
              sx={{ background: 'linear-gradient(135deg, #6366f1 0%, #8b5cf6 100%)' }}
            >
              Add Material
            </Button>
          </Box>

          <TableContainer component={Paper}>
            <Table>
              <TableHead>
                <TableRow>
                  <TableCell>Title</TableCell>
                  <TableCell>Category</TableCell>
                  <TableCell>Type</TableCell>
                  <TableCell>Difficulty</TableCell>
                  <TableCell>Duration</TableCell>
                  <TableCell align="right">Actions</TableCell>
                </TableRow>
              </TableHead>
              <TableBody>
                {filteredMaterials.map((mat) => {
                  const typeConfig = contentTypes.find(t => t.value === mat.content_type) || contentTypes[contentTypes.length - 1];
                  const TypeIcon = typeConfig.icon;
                  
                  return (
                    <TableRow key={mat.id} hover>
                      <TableCell>
                        <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                          <TypeIcon sx={{ color: typeConfig.color, fontSize: 20 }} />
                          <Box>
                            <Typography variant="body2" fontWeight={600}>{mat.title}</Typography>
                            {mat.author && (
                              <Typography variant="caption" color="text.secondary">
                                by {mat.author}
                              </Typography>
                            )}
                          </Box>
                        </Box>
                      </TableCell>
                      <TableCell>
                        <Chip 
                          label={mat.TrainingCategory?.name || '-'} 
                          size="small"
                          sx={{ bgcolor: mat.TrainingCategory?.color + '20', color: mat.TrainingCategory?.color }}
                        />
                      </TableCell>
                      <TableCell>
                        <Chip label={typeConfig.label} size="small" sx={{ bgcolor: typeConfig.color + '20', color: typeConfig.color }} />
                      </TableCell>
                      <TableCell>
                        <Chip label={mat.difficulty_level} size="small" color={difficultyColors[mat.difficulty_level]} />
                      </TableCell>
                      <TableCell>{mat.duration_minutes ? `${mat.duration_minutes} min` : '-'}</TableCell>
                      <TableCell align="right">
                        {mat.content_url && (
                          <Tooltip title="Open Content">
                            <IconButton size="small" href={mat.content_url} target="_blank">
                              <LinkIcon fontSize="small" />
                            </IconButton>
                          </Tooltip>
                        )}
                        <IconButton size="small" onClick={() => handleEditMaterial(mat)}>
                          <EditIcon fontSize="small" />
                        </IconButton>
                        <IconButton size="small" color="error" onClick={() => handleDeleteMaterial(mat.id)}>
                          <DeleteIcon fontSize="small" />
                        </IconButton>
                      </TableCell>
                    </TableRow>
                  );
                })}
                {filteredMaterials.length === 0 && (
                  <TableRow>
                    <TableCell colSpan={6} sx={{ textAlign: 'center', py: 4 }}>
                      No materials found
                    </TableCell>
                  </TableRow>
                )}
              </TableBody>
            </Table>
          </TableContainer>
        </Box>
      )}

      {/* YouTube Library Tab */}
      {activeTab === 3 && (
        <TrainingYoutubeLibrary categories={categories} />
      )}

      {/* Exams Tab */}
      {activeTab === 4 && (
        <TrainingExamAdmin categories={categories} />
      )}

      {/* Assignments Tab */}
      {activeTab === 5 && (
        <Box>
          <Box sx={{ display: 'flex', justifyContent: 'flex-end', mb: 2 }}>
            <Button 
              variant="contained" 
              startIcon={<AddIcon />} 
              onClick={() => setAssignDialog(true)}
              sx={{ background: 'linear-gradient(135deg, #6366f1 0%, #8b5cf6 100%)' }}
            >
              Assign Training
            </Button>
          </Box>

          <TableContainer component={Paper}>
            <Table>
              <TableHead>
                <TableRow>
                  <TableCell>User</TableCell>
                  <TableCell>Training</TableCell>
                  <TableCell>Type</TableCell>
                  <TableCell>Progress</TableCell>
                  <TableCell>Status</TableCell>
                  <TableCell>Enrolled</TableCell>
                </TableRow>
              </TableHead>
              <TableBody>
                {enrollments.map((e) => (
                  <TableRow key={e.id} hover>
                    <TableCell>
                      <Typography variant="body2" fontWeight={600}>
                        {e.user?.first_name} {e.user?.last_name}
                      </Typography>
                      <Typography variant="caption" color="text.secondary">{e.user?.email}</Typography>
                    </TableCell>
                    <TableCell>{e.category?.name}</TableCell>
                    <TableCell>
                      <Chip 
                        label={e.enrollment_type === 'assigned' ? 'Assigned' : 'Self-enrolled'} 
                        size="small"
                        color={e.enrollment_type === 'assigned' ? 'primary' : 'default'}
                        variant="outlined"
                      />
                    </TableCell>
                    <TableCell>
                      <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                        <Box sx={{ flex: 1, maxWidth: 100 }}>
                          <Box sx={{ height: 6, borderRadius: 3, bgcolor: 'grey.200', overflow: 'hidden' }}>
                            <Box sx={{ height: '100%', width: `${e.progress_percentage}%`, bgcolor: e.status === 'completed' ? 'success.main' : 'primary.main' }} />
                          </Box>
                        </Box>
                        <Typography variant="caption">{e.progress_percentage}%</Typography>
                      </Box>
                    </TableCell>
                    <TableCell>
                      <Chip 
                        label={e.status} 
                        size="small"
                        color={e.status === 'completed' ? 'success' : e.status === 'in_progress' ? 'info' : 'default'}
                      />
                    </TableCell>
                    <TableCell>{new Date(e.enrolled_at).toLocaleDateString()}</TableCell>
                  </TableRow>
                ))}
                {enrollments.length === 0 && (
                  <TableRow>
                    <TableCell colSpan={6} sx={{ textAlign: 'center', py: 4 }}>
                      No enrollments yet
                    </TableCell>
                  </TableRow>
                )}
              </TableBody>
            </Table>
          </TableContainer>
        </Box>
      )}

      {/* Category Dialog */}
      <Dialog open={categoryDialog} onClose={() => { setCategoryDialog(false); setEditingCategoryId(null); setCategoryErrors({}); }} maxWidth="sm" fullWidth>
        <DialogTitle>{editingCategoryId ? 'Edit Category' : 'Add Category'}</DialogTitle>
        <DialogContent>
          <Grid container spacing={2} sx={{ mt: 1 }}>
            <Grid item xs={12}>
              <TextField 
                fullWidth 
                label={<RequiredLabel>Name</RequiredLabel>}
                value={categoryForm.name} 
                onChange={(e) => {
                  setCategoryForm({ ...categoryForm, name: e.target.value });
                  if (categoryErrors.name) setCategoryErrors({});
                }} 
                required
                disabled={!!(editingCategoryId && categories.find((c) => c.id === editingCategoryId)?.is_system_category)}
                error={!!categoryErrors.name}
                helperText={categoryErrors.name ? 'Name is required' : ''}
                sx={getFieldErrorSx(categoryErrors.name)}
              />
            </Grid>
            <Grid item xs={12}>
              <TextField 
                fullWidth 
                label="Description" 
                value={categoryForm.description} 
                onChange={(e) => setCategoryForm({ ...categoryForm, description: e.target.value })} 
                multiline 
                rows={2} 
              />
            </Grid>
            <Grid item xs={12}>
              <TextField 
                fullWidth 
                label="Color" 
                type="color"
                value={categoryForm.color} 
                onChange={(e) => setCategoryForm({ ...categoryForm, color: e.target.value })}
                InputProps={{
                  startAdornment: (
                    <InputAdornment position="start">
                      <Box sx={{ width: 24, height: 24, borderRadius: 1, bgcolor: categoryForm.color }} />
                    </InputAdornment>
                  )
                }}
              />
            </Grid>
          </Grid>
        </DialogContent>
        <DialogActions>
          <Button onClick={() => { setCategoryDialog(false); setEditingCategoryId(null); }}>Cancel</Button>
          <Button variant="contained" onClick={handleSaveCategory} disabled={!categoryForm.name}>Save</Button>
        </DialogActions>
      </Dialog>

      {/* Material Dialog */}
      <Dialog open={materialDialog} onClose={() => { setMaterialDialog(false); setEditingMaterialId(null); setMaterialErrors({}); }} maxWidth="md" fullWidth>
        <DialogTitle>{editingMaterialId ? 'Edit Material' : 'Add Material'}</DialogTitle>
        <DialogContent>
          <Grid container spacing={2} sx={{ mt: 1 }}>
            <Grid item xs={12}>
              <TextField 
                fullWidth 
                label={<RequiredLabel>Title</RequiredLabel>}
                value={materialForm.title} 
                onChange={(e) => handleMaterialFormChange('title', e.target.value)} 
                required 
                error={!!materialErrors.title}
                helperText={materialErrors.title ? 'Title is required' : ''}
                sx={getFieldErrorSx(materialErrors.title)}
              />
            </Grid>
            <Grid item xs={12}>
              <TextField 
                fullWidth 
                label="Description" 
                value={materialForm.description} 
                onChange={(e) => handleMaterialFormChange('description', e.target.value)} 
                multiline 
                rows={2} 
              />
            </Grid>
            <Grid item xs={12} md={6}>
              <FormControl fullWidth required error={!!materialErrors.category_id}>
                <InputLabel><RequiredLabel>Category</RequiredLabel></InputLabel>
                <Select 
                  value={materialForm.category_id} 
                  label="Category *" 
                  onChange={(e) => handleMaterialFormChange('category_id', e.target.value)}
                  sx={getSelectErrorSx(materialErrors.category_id)}
                >
                  {categories.filter((c) => !c.is_system_category).map(c => <MenuItem key={c.id} value={c.id}>{c.name}</MenuItem>)}
                </Select>
                {materialErrors.category_id && <FormHelperText>Category is required</FormHelperText>}
              </FormControl>
            </Grid>
            <Grid item xs={12} md={6}>
              <FormControl fullWidth required error={!!materialErrors.content_type}>
                <InputLabel><RequiredLabel>Content Type</RequiredLabel></InputLabel>
                <Select 
                  value={materialForm.content_type} 
                  label="Content Type *" 
                  onChange={(e) => handleMaterialFormChange('content_type', e.target.value)}
                  sx={getSelectErrorSx(materialErrors.content_type)}
                >
                  {contentTypes.map(t => {
                    const Icon = t.icon;
                    return (
                      <MenuItem key={t.value} value={t.value}>
                        <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                          <Icon sx={{ color: t.color, fontSize: 18 }} />
                          {t.label}
                        </Box>
                      </MenuItem>
                    );
                  })}
                </Select>
                {materialErrors.content_type && <FormHelperText>Content Type is required</FormHelperText>}
              </FormControl>
            </Grid>
            <Grid item xs={12}>
              <TextField 
                fullWidth 
                label="Content URL" 
                value={materialForm.content_url} 
                onChange={(e) => setMaterialForm({ ...materialForm, content_url: e.target.value })}
                placeholder="https://..."
              />
            </Grid>
            <Grid item xs={12} md={6}>
              <TextField 
                fullWidth 
                label="Author/Instructor" 
                value={materialForm.author} 
                onChange={(e) => setMaterialForm({ ...materialForm, author: e.target.value })}
              />
            </Grid>
            <Grid item xs={6} md={3}>
              <TextField 
                fullWidth 
                label="Duration (min)" 
                type="number" 
                value={materialForm.duration_minutes} 
                onChange={(e) => setMaterialForm({ ...materialForm, duration_minutes: e.target.value })}
              />
            </Grid>
            <Grid item xs={6} md={3}>
              <FormControl fullWidth>
                <InputLabel>Difficulty</InputLabel>
                <Select 
                  value={materialForm.difficulty_level} 
                  label="Difficulty" 
                  onChange={(e) => setMaterialForm({ ...materialForm, difficulty_level: e.target.value })}
                >
                  <MenuItem value="beginner">Beginner</MenuItem>
                  <MenuItem value="intermediate">Intermediate</MenuItem>
                  <MenuItem value="advanced">Advanced</MenuItem>
                </Select>
              </FormControl>
            </Grid>
            <Grid item xs={12}>
              <TextField 
                fullWidth 
                label="Tags (comma-separated)" 
                value={materialForm.tags} 
                onChange={(e) => setMaterialForm({ ...materialForm, tags: e.target.value })}
                placeholder="AI, Machine Learning, Python"
              />
            </Grid>
          </Grid>
        </DialogContent>
        <DialogActions>
          <Button onClick={() => { setMaterialDialog(false); setEditingMaterialId(null); }}>Cancel</Button>
          <Button variant="contained" onClick={handleSaveMaterial} disabled={!materialForm.title || !materialForm.category_id}>Save</Button>
        </DialogActions>
      </Dialog>

      {/* Assign Dialog */}
      <Dialog open={assignDialog} onClose={() => setAssignDialog(false)} maxWidth="sm" fullWidth>
        <DialogTitle>Assign Training</DialogTitle>
        <DialogContent>
          <Grid container spacing={2} sx={{ mt: 1 }}>
            <Grid item xs={12}>
              <FormControl fullWidth required>
                <InputLabel>User</InputLabel>
                <Select 
                  value={assignForm.user_id} 
                  label="User" 
                  onChange={(e) => setAssignForm({ ...assignForm, user_id: e.target.value })}
                >
                  {users.map(u => (
                    <MenuItem key={u.id} value={u.id}>{u.first_name} {u.last_name} ({u.email})</MenuItem>
                  ))}
                </Select>
              </FormControl>
            </Grid>
            <Grid item xs={12}>
              <FormControl fullWidth required>
                <InputLabel>Training Category</InputLabel>
                <Select 
                  value={assignForm.category_id} 
                  label="Training Category" 
                  onChange={(e) => setAssignForm({ ...assignForm, category_id: e.target.value })}
                >
                  {categories.filter((c) => !c.is_system_category).map(c => <MenuItem key={c.id} value={c.id}>{c.name}</MenuItem>)}
                </Select>
              </FormControl>
            </Grid>
            <Grid item xs={12}>
              <TextField 
                fullWidth 
                label="Due Date" 
                type="date" 
                value={assignForm.due_date} 
                onChange={(e) => setAssignForm({ ...assignForm, due_date: e.target.value })} 
                InputLabelProps={{ shrink: true }}
              />
            </Grid>
          </Grid>
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setAssignDialog(false)}>Cancel</Button>
          <Button variant="contained" onClick={handleAssign} disabled={!assignForm.user_id || !assignForm.category_id}>
            Assign
          </Button>
        </DialogActions>
      </Dialog>
    </Box>
  );
};

export default TrainingMaterials;
